import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { API_URL } from '../config/api';
import { adminFetch } from '../utils/adminFetch';
import { isRemembered, remember } from '../utils/affiliateAuth';
import SchedulerTab from './SchedulerTab';
import './AffiliateStats.css';

interface AffiliateStatsData {
  promoCode: {
    code: string;
    couponId: string;
    timesRedeemed: number;
    totalRevenue: number;
    totalDiscountGiven: number;
  } | null;
  survey: {
    handle: string;
    totalSignups: number;
    paidCount: number;
  } | null;
  refLink: {
    handle: string;
    totalSignups: number;
    paidCount: number;
  } | null;
  totalCustomers: number;
  countryDistribution?: { country: string; count: number }[];
  breakdown: {
    promoCode: number;
    promoCodeRevenue: number;
    surveyOnly: number;
    surveyOnlyRevenue: number;
    refOnly: number;
    refOnlyRevenue: number;
  };
  totalRevenue: number;
  bonus: number;
  bonusBreakdown?: {
    legacy: { rate: number; revenue: number; bonus: number };
    current: { rate: number; revenue: number; bonus: number };
    cutoff: string;
  };
  blendedRate?: number;
}

// 설문 + 추적 링크 가입 합계. /affiliates 표·affiliate-payouts API와 같은 기준이다.
const totalSignupsOf = (s: { survey: { totalSignups: number } | null; refLink: { totalSignups: number } | null }) =>
  (s.survey?.totalSignups ?? 0) + (s.refLink?.totalSignups ?? 0);

interface Payout {
  _id: string;
  amount: number;
  date: string;
  note: string;
  kind?: 'retainer' | 'bonus';
  week?: number;
  sourceAccount?: string;  // 이 지급이 속한 IG 계정(계정 전환자는 여러 개). 계정별 주차 구분.
}

interface PayoutData {
  totalPaid: number;
  totalRetainer?: number;
  totalBonus?: number;
  bonusEarned?: number | null;    // 총 발생 bonus(매출 기준)
  bonusPending?: number | null;   // 미지급 = bonusEarned − totalBonus
  retainerPending?: number | null;    // 밀린 기본급(완료 주차 기준)
  retainerOwedWeeks?: number[];       // 밀린 주차 배열
  contractEndDate?: string | null;
  payouts: Payout[];
  retainer?: Payout[];
  bonus?: Payout[];
}

interface AffiliateStatsProps {
  // route만 남긴다(동적 라우팅에선 URL useParams가 정본, props는 하위호환 폴백).
  // handle/promoCode/searchKeyword/password는 GET /affiliate-meta/:route로 DB에서 받는다.
  route?: string;
}

interface DailyRow {
  date: string;
  signups: number;
  paid: number;
  revenue: number;
  bonus: number;
  rate?: number; // applied bonus rate for that day (totals row has none)
}

interface DailyData {
  year: number;
  month: number;
  days: DailyRow[];
  totals: DailyRow;
  rateCutoff: string | null; // YYYY-MM-DD when rate increased (joe only), else null
  promoSignupsTracked: boolean;
}

interface AudienceImage {
  id: string;
  imageUrl: string;
  uploadedAt?: string | null;
}

interface SurveySignup {
  // Opaque per-affiliate id. The user's real email is intentionally never sent
  // to the client so an affiliate can't scrape/poach the audience.
  token: string;
  surveyHandle: string;
  howDidYouHear: string;
  source: 'handle' | 'how_did_you_hear';
  signupDate: string | null;
  paid: boolean;
  excluded: boolean;
  exclusionId: string | null;
}

const AUDIENCE_IMAGE_MAX = 10;

const formatUSD = (amount: number): string =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Whole-percent label, e.g. 0.3 -> "30%"
const pct = (rate: number): string => `${Math.round(rate * 100)}%`;

// ISO 3166-1 alpha-2 country code (e.g. "ES") -> flag emoji ("🇪🇸") by mapping
// each letter to its regional indicator symbol. Returns '' for empty/invalid.
const countryToFlag = (code: string): string => {
  const cc = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
};

// "ES" -> "Spain" for a readable tooltip. Falls back to the raw code.
const countryName = (code: string): string => {
  const cc = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return cc;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(cc) || cc;
  } catch {
    return cc;
  }
};

// "2026-06-29" -> "Jun 29" (parsed as UTC to match the cutoff's UTC boundary)
const formatShortDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00.000Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC'
  });

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Post-cutoff bonus rate (mirrors backend JOE_BONUS_RATE) — used to flag/highlight
// the increased-rate rows in the daily table.
const JOE_BONUS_RATE = 0.30;

// Stripe Connect Express self-serve payout 지원국 (백엔드 SUPPORTED_PAYOUT_COUNTRIES와 동기화).
const PAYOUT_COUNTRIES: { code: string; name: string }[] = [
  { code: 'US', name: 'United States' }, { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'IE', name: 'Ireland' }, { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' }, { code: 'ES', name: 'Spain' }, { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' }, { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' }, { code: 'PT', name: 'Portugal' }, { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' }, { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' }, { code: 'CZ', name: 'Czechia' }, { code: 'RO', name: 'Romania' },
  { code: 'GR', name: 'Greece' }, { code: 'HU', name: 'Hungary' }, { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' }, { code: 'SK', name: 'Slovakia' }, { code: 'SI', name: 'Slovenia' },
  { code: 'LT', name: 'Lithuania' }, { code: 'LV', name: 'Latvia' }, { code: 'EE', name: 'Estonia' },
  { code: 'LU', name: 'Luxembourg' }, { code: 'CY', name: 'Cyprus' }, { code: 'MT', name: 'Malta' },
  { code: 'AU', name: 'Australia' }, { code: 'NZ', name: 'New Zealand' }, { code: 'JP', name: 'Japan' },
  { code: 'SG', name: 'Singapore' }, { code: 'HK', name: 'Hong Kong' }, { code: 'BR', name: 'Brazil' }
];

const AffiliateStats: React.FC<AffiliateStatsProps> = (props) => {
  // route는 URL에서(동적 라우팅). App이 props로 넘기던 방식과 호환 위해 props도 폴백으로 받는다.
  const { route: routeParam } = useParams<{ route?: string }>();
  const route = (routeParam || props.route || '').toLowerCase();

  // ⭐ 매출귀속 키(handle/promoCode/searchKeyword)와 페이지 비번의 정본 = DB(GET /affiliate-meta/:route).
  // 예전엔 config/affiliates.ts → App.tsx props였다. 이제 마운트 시 route로 이 API를 호출해 받는다.
  // meta 도착 전에는 로딩, 404면 not-found 화면. (config 파일 폐지 — DB 단권화.)
  const [meta, setMeta] = useState<{ handle: string; igHandle: string; promoCode: string; searchKeyword: string; password: string } | null>(null);
  const [metaError, setMetaError] = useState<'notfound' | 'network' | null>(null);
  const handle = meta?.handle || '';                 // API 파라미터·payout 저장용(=route, 기존 데이터 일관)
  const igHandle = meta?.igHandle || meta?.handle || ''; // 화면 @표시·IG 링크 전용(실제 IG 핸들)
  const promoCode = meta?.promoCode || '';
  const searchKeyword = meta?.searchKeyword || '';
  const correctPassword = meta?.password || '';

  // 비번 한 번 통과하면 이후 방문(새로고침·새 탭·다른 어필리에이트 페이지)에서 다시 안 묻는다.
  // 네임스페이스 'affiliate'로 저장 — 같은 AFFILIATE_PASSWORD를 쓰는 모든 개별 페이지가 공유.
  // meta 도착 후 correctPassword가 채워지면 재평가되도록 effect로 세팅(초기엔 false).
  const [authed, setAuthed] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState<AffiliateStatsData | null>(null);
  const [payoutData, setPayoutData] = useState<PayoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Daily breakdown with monthly navigation (UTC-based to match backend)
  const nowUtc = new Date();
  const [viewYear, setViewYear] = useState(nowUtc.getUTCFullYear());
  const [viewMonth, setViewMonth] = useState(nowUtc.getUTCMonth() + 1); // 1-12
  const [daily, setDaily] = useState<DailyData | null>(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  // Audience-distribution images (multiple per handle, drag-and-drop upload)
  const [audienceImages, setAudienceImages] = useState<AudienceImage[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Lightbox: URL of the image shown enlarged, or null when closed
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Inline note editing
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');

  // Inline amount editing
  const [editingAmountId, setEditingAmountId] = useState<string | null>(null);
  const [editingAmountValue, setEditingAmountValue] = useState('');

  // Payout form
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutDate, setPayoutDate] = useState(new Date().toISOString().split('T')[0]);
  const [payoutNote, setPayoutNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Survey signups modal (review + exclude false positives)
  const [signupsOpen, setSignupsOpen] = useState(false);
  const [signups, setSignups] = useState<SurveySignup[]>([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [mutatingToken, setMutatingToken] = useState<string | null>(null);

  // Editable display handles (extra Instagram handles shown at the top, in
  // addition to the immutable base `handle`). Removed ones move to history.
  const [displayHandles, setDisplayHandles] = useState<string[]>([]);
  const [handleHistory, setHandleHistory] = useState<{ handle: string; removedAt: string }[]>([]);
  // Affiliate's own country (ISO code, e.g. "ES"), enriched from the recruiting
  // server. Empty when unknown. Shown as a flag emoji next to the handle.
  const [country, setCountry] = useState<string>('');
  const [newHandle, setNewHandle] = useState('');
  const [addingHandle, setAddingHandle] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Admin-only actions (editing handles, adding payouts) require a second,
  // admin-only password (separate from the page password). Once entered
  // correctly it's remembered for this session so the admin isn't prompted on
  // every action.
  const EDIT_ADMIN_PASSWORD = '0987';
  const [editAuthed, setEditAuthed] = useState(false);
  // 상세 탭: overview(기존 대시보드) / scheduler(📅 스케줄러+초안) / payouts(💰 정산).
  // URL과 동기화: /{route}/overview|schedule|payouts. URL은 'schedule'(사람친화), state는 'scheduler'.
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const urlToTab = (u?: string): 'overview' | 'scheduler' | 'payouts' => {
    if (u === 'schedule' || u === 'scheduler') return 'scheduler';
    if (u === 'payouts') return 'payouts';
    return 'overview';
  };
  const tabToUrl = (t: 'overview' | 'scheduler' | 'payouts'): string =>
    t === 'scheduler' ? 'schedule' : t;
  const activeTab = urlToTab(tabParam);
  // 탭 전환 = URL 이동. route를 모르면(직접 임베드 등) state 폴백 대신 그대로.
  const setActiveTab = (t: 'overview' | 'scheduler' | 'payouts') => {
    if (route) navigate(`/${route}/${tabToUrl(t)}`);
  };
  // Payouts 세부탭: retainer(주급) / bonus(인센티브).
  const [payoutKind, setPayoutKind] = useState<'retainer' | 'bonus'>('retainer');
  // Stripe payout 온보딩 (크리에이터 셀프서브). 상태 + 국가선택.
  const [payoutStatus, setPayoutStatus] = useState<{ onboarded?: boolean; payoutsEnabled?: boolean; detailsSubmitted?: boolean; notStarted?: boolean; country?: string | null } | null>(null);
  const [onboardCountry, setOnboardCountry] = useState('');
  const [onboarding, setOnboarding] = useState(false);
  const ensureEditAuth = (): boolean => {
    if (editAuthed) return true;
    const entered = window.prompt('Enter admin password:');
    if (entered === null) return false; // cancelled
    if (entered === EDIT_ADMIN_PASSWORD) {
      setEditAuthed(true);
      return true;
    }
    alert('Incorrect admin password');
    return false;
  };

  // Stripe payout 상태 조회 (payouts 탭 열릴 때). 페이지 pw로 게이트.
  const loadPayoutStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/stripe/ugc/payout/self-status?handle=${encodeURIComponent(handle)}&password=${correctPassword}`);
      const j = await r.json();
      if (j.success) setPayoutStatus(j.data);
    } catch { /* 조회 실패 시 섹션은 not-started로 폴백 */ }
  };
  useEffect(() => {
    if (authed && activeTab === 'payouts' && !payoutStatus) loadPayoutStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, activeTab]);

  // 계좌 연결 시작. "Connect"(최초)는 국가 선택 필수지만, "Continue setup"(이미 계정 생성됨)은
  // 국가가 이미 정해져 있고 화면에 드롭다운이 없다 → 국가 재입력을 요구하면 안 된다(요구하면
  // "국가를 선택하라는데 선택 옵션이 없다"고 막힘 — nocanonico·Andrea 실측 버그). 이미 시작한
  // 경우엔 country를 생략해 백엔드가 저장된 payoutCountry를 쓰게 한다.
  const alreadyStarted = !!(payoutStatus && !payoutStatus.notStarted);
  const startPayoutOnboarding = async () => {
    if (!alreadyStarted && !onboardCountry) { alert('Please select your country first.'); return; }
    setOnboarding(true);
    try {
      const r = await fetch(`${API_URL}/api/stripe/ugc/payout/self-onboard`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle, ...(onboardCountry ? { country: onboardCountry } : {}), password: correctPassword })
      });
      const j = await r.json();
      if (j.success && j.data?.url) {
        window.location.href = j.data.url;   // Stripe 온보딩 페이지로 이동
      } else {
        alert(j.error === undefined ? 'Failed to start onboarding' : `Failed: ${j.error}`);
      }
    } catch { alert('Failed to start onboarding'); } finally { setOnboarding(false); }
  };

  // 계약종료일 수정 (관리자만). date input의 onChange에서 값(YYYY-MM-DD 또는 '')을 받아 저장.
  // 계약 종료일 편집은 관리자 통합 overview(/affiliates)로 이관 — 크리에이터 개별 페이지에선
  // 계약 기간을 숨긴다(2026-08-28, "term through" 표시가 장기계약자 불안 유발). 편집 핸들러 제거.

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-profile/${encodeURIComponent(handle)}`);
      const json = await res.json();
      if (json.success) {
        setDisplayHandles(json.data.displayHandles || []);
        setHandleHistory(json.data.handleHistory || []);
        setCountry(json.data.country || '');
      }
    } catch {
      // non-critical; the page still works without extra handles
    }
  };

  const addHandle = async () => {
    const value = newHandle.trim().replace(/^@/, '');
    if (!value || addingHandle) return;
    if (!ensureEditAuth()) return;
    setAddingHandle(true);
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-profile/${encodeURIComponent(handle)}/handles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: value })
      });
      const json = await res.json();
      if (json.success) {
        setDisplayHandles(json.data.displayHandles || []);
        setHandleHistory(json.data.handleHistory || []);
        setNewHandle('');
      } else {
        alert(json.error || 'Failed to add handle');
      }
    } catch {
      alert('Failed to add handle');
    } finally {
      setAddingHandle(false);
    }
  };

  // Remove a display handle. It's kept in history, not deleted outright.
  const removeHandle = async (value: string) => {
    if (!ensureEditAuth()) return;
    if (!window.confirm(`Remove @${value}? It will be kept in history.`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/stripe/affiliate-profile/${encodeURIComponent(handle)}/handles/${encodeURIComponent(value)}`,
        { method: 'DELETE' }
      );
      const json = await res.json();
      if (json.success) {
        setDisplayHandles(json.data.displayHandles || []);
        setHandleHistory(json.data.handleHistory || []);
      } else {
        alert(json.error || 'Failed to remove handle');
      }
    } catch {
      alert('Failed to remove handle');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, payoutsRes] = await Promise.all([
        fetch(`${API_URL}/api/stripe/affiliate-stats?promoCode=${promoCode}&handle=${handle}&searchKeyword=${encodeURIComponent(searchKeyword)}`),
        fetch(`${API_URL}/api/stripe/affiliate-payouts/${handle}?promoCode=${encodeURIComponent(promoCode)}&searchKeyword=${encodeURIComponent(searchKeyword)}`)
      ]);
      const statsData = await statsRes.json();
      const payoutsData = await payoutsRes.json();

      if (statsData.success) setStats(statsData.data);
      else setError(statsData.error || 'Failed to load stats');

      if (payoutsData.success) setPayoutData(payoutsData.data);
    } catch {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchDaily = async (year: number, month: number) => {
    setDailyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-daily?promoCode=${promoCode}&handle=${handle}&searchKeyword=${encodeURIComponent(searchKeyword)}&year=${year}&month=${month}`);
      const json = await res.json();
      if (json.success) setDaily(json.data);
    } catch {
      // keep prior table on failure
    } finally {
      setDailyLoading(false);
    }
  };

  const fetchAudienceImage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-image/${handle}`);
      const json = await res.json();
      if (json.success) setAudienceImages(json.data.images || []);
    } catch {
      // ignore
    }
  };

  const fetchSignups = async () => {
    setSignupsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-survey-signups?handle=${handle}&searchKeyword=${encodeURIComponent(searchKeyword)}`);
      const json = await res.json();
      if (json.success) setSignups(json.data.signups || []);
    } catch {
      // keep prior list on failure
    } finally {
      setSignupsLoading(false);
    }
  };

  const openSignups = () => {
    setSignupsOpen(true);
    fetchSignups();
  };

  // Toggle exclusion for one survey signup, then refresh both the modal list and
  // the top-level stats/daily so all numbers reflect the change.
  const toggleExclusion = async (s: SurveySignup) => {
    setMutatingToken(s.token);
    try {
      if (s.excluded) {
        await adminFetch(`${API_URL}/api/stripe/affiliate-survey-exclusions`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ handle, token: s.token })
        });
      } else {
        await adminFetch(`${API_URL}/api/stripe/affiliate-survey-exclusions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            handle,
            searchKeyword,
            token: s.token
          })
        });
      }
      await Promise.all([fetchSignups(), fetchData(), fetchDaily(viewYear, viewMonth)]);
    } catch {
      // ignore; user can retry
    } finally {
      setMutatingToken(null);
    }
  };

  // 마운트/route 변경 시 meta(귀속키+비번)를 DB에서 받는다. 404=존재하지 않는 어필리에이트.
  useEffect(() => {
    if (!route) { setMetaError('notfound'); return; }
    let alive = true;
    setMeta(null); setMetaError(null);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/affiliate-meta/${encodeURIComponent(route)}`);
        if (res.status === 404) { if (alive) setMetaError('notfound'); return; }
        const json = await res.json();
        if (!alive) return;
        if (json.success && json.data) {
          setMeta({
            handle: json.data.handle || '',
            igHandle: json.data.igHandle || json.data.handle || '',
            promoCode: json.data.promoCode || '',
            searchKeyword: json.data.searchKeyword || '',
            password: json.data.password || ''
          });
        } else {
          setMetaError('network');
        }
      } catch {
        if (alive) setMetaError('network');
      }
    })();
    return () => { alive = false; };
  }, [route]);

  // meta 도착 후 페이지 비번이 확정되면, 기억된 인증(localStorage)을 재평가한다.
  useEffect(() => {
    if (correctPassword && isRemembered('affiliate', correctPassword)) {
      setAuthed(true);
    }
  }, [correctPassword]);

  useEffect(() => {
    if (authed) {
      fetchData();
      fetchAudienceImage();
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  useEffect(() => {
    if (authed) fetchDaily(viewYear, viewMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, viewYear, viewMonth]);

  // Close the lightbox on Escape
  useEffect(() => {
    if (!lightboxUrl) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightboxUrl(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxUrl]);

  const uploadAudienceImages = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) {
      alert('Please drop image files.');
      return;
    }
    if (audienceImages.length + files.length > AUDIENCE_IMAGE_MAX) {
      alert(`Max ${AUDIENCE_IMAGE_MAX} images. You can add ${AUDIENCE_IMAGE_MAX - audienceImages.length} more.`);
      return;
    }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('handle', handle);
      for (const file of files) formData.append('image', file);
      const res = await fetch(`${API_URL}/api/stripe/affiliate-image`, {
        method: 'POST',
        body: formData
      });
      const json = await res.json();
      if (json.success) setAudienceImages(json.data.images || []);
      else alert(json.error || 'Failed to upload image');
    } catch {
      alert('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) uploadAudienceImages(files);
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-image/${handle}/${encodeURIComponent(imageId)}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) setAudienceImages(json.data.images || []);
      else alert(json.error || 'Failed to delete image');
    } catch {
      alert('Failed to delete image');
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === correctPassword) {
      setAuthed(true);
      remember('affiliate', correctPassword);
    } else {
      alert('Incorrect password');
    }
  };

  const handleAddPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payoutAmount || Number(payoutAmount) <= 0) return;
    if (!ensureEditAuth()) return;
    setSubmitting(true);
    try {
      const res = await adminFetch(`${API_URL}/api/stripe/affiliate-payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle,
          kind: payoutKind,             // 현재 세부탭(retainer/bonus)으로 추가
          amount: Number(payoutAmount),
          date: payoutDate,
          note: payoutNote
        })
      });
      const data = await res.json();
      if (data.success) {
        setPayoutAmount('');
        setPayoutNote('');
        setPayoutDate(new Date().toISOString().split('T')[0]);
        const payoutsRes = await fetch(`${API_URL}/api/stripe/affiliate-payouts/${handle}?promoCode=${encodeURIComponent(promoCode)}&searchKeyword=${encodeURIComponent(searchKeyword)}`);
        const payoutsData = await payoutsRes.json();
        if (payoutsData.success) setPayoutData(payoutsData.data);
      }
    } catch {
      alert('Failed to add payout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNoteDoubleClick = (payout: Payout) => {
    if (!ensureEditAuth()) return;
    setEditingNoteId(payout._id);
    setEditingNoteValue(payout.note || '');
  };

  const handleNoteSave = async (payoutId: string) => {
    try {
      const res = await adminFetch(`${API_URL}/api/stripe/affiliate-payouts/${payoutId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: editingNoteValue })
      });
      const data = await res.json();
      if (data.success) {
        const payoutsRes = await fetch(`${API_URL}/api/stripe/affiliate-payouts/${handle}?promoCode=${encodeURIComponent(promoCode)}&searchKeyword=${encodeURIComponent(searchKeyword)}`);
        const payoutsData = await payoutsRes.json();
        if (payoutsData.success) setPayoutData(payoutsData.data);
      }
    } catch {
      alert('Failed to update note');
    } finally {
      setEditingNoteId(null);
    }
  };

  const handleAmountDoubleClick = (payout: Payout) => {
    if (!ensureEditAuth()) return;
    setEditingAmountId(payout._id);
    setEditingAmountValue(String(payout.amount));
  };

  const handleAmountSave = async (payoutId: string) => {
    const parsed = Number(editingAmountValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert('Invalid amount');
      setEditingAmountId(null);
      return;
    }
    try {
      const res = await adminFetch(`${API_URL}/api/stripe/affiliate-payouts/${payoutId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parsed })
      });
      const data = await res.json();
      if (data.success) {
        const payoutsRes = await fetch(`${API_URL}/api/stripe/affiliate-payouts/${handle}?promoCode=${encodeURIComponent(promoCode)}&searchKeyword=${encodeURIComponent(searchKeyword)}`);
        const payoutsData = await payoutsRes.json();
        if (payoutsData.success) setPayoutData(payoutsData.data);
      }
    } catch {
      alert('Failed to update amount');
    } finally {
      setEditingAmountId(null);
    }
  };

  const goPrevMonth = () => {
    if (viewMonth === 1) {
      setViewYear(y => y - 1);
      setViewMonth(12);
    } else {
      setViewMonth(m => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 12) {
      setViewYear(y => y + 1);
      setViewMonth(1);
    } else {
      setViewMonth(m => m + 1);
    }
  };

  const isCurrentMonth =
    viewYear === nowUtc.getUTCFullYear() && viewMonth === nowUtc.getUTCMonth() + 1;

  // 존재하지 않는 route(오타/삭제된 어필리에이트) — 동적 라우팅 catch-all 폴백.
  if (metaError === 'notfound') {
    return (
      <div className="affiliate-stats">
        <div className="affiliate-stats__login">
          <h1 className="affiliate-stats__title">Affiliate not found</h1>
          <p className="affiliate-stats__loading">이 주소의 어필리에이트를 찾을 수 없습니다.</p>
          <a className="affiliate-stats__btn" href="/affiliates" style={{ textDecoration: 'none' }}>Go to overview</a>
        </div>
      </div>
    );
  }

  // meta 로드 실패(네트워크/서버) — 재시도.
  if (metaError === 'network') {
    return (
      <div className="affiliate-stats">
        <div className="affiliate-stats__login">
          <h1 className="affiliate-stats__title">일시적 연결 문제</h1>
          <p className="affiliate-stats__loading">잠시 후 다시 시도해 주세요.</p>
          <button className="affiliate-stats__btn" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  // meta 아직 로딩 중 — handle/비번을 모르므로 로그인 폼을 아직 못 그린다.
  if (!meta) {
    return (
      <div className="affiliate-stats">
        <p className="affiliate-stats__loading">Loading...</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="affiliate-stats">
        <form className="affiliate-stats__login" onSubmit={handleLogin}>
          <h1 className="affiliate-stats__title">@{igHandle}</h1>
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="affiliate-stats__input"
            autoFocus
          />
          <button type="submit" className="affiliate-stats__btn">Enter</button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="affiliate-stats">
        <p className="affiliate-stats__loading">Loading...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="affiliate-stats">
        <p className="affiliate-stats__error">{error || 'No data available'}</p>
      </div>
    );
  }

  const totalPaid = payoutData?.totalPaid ?? 0;
  // 총 미지급 = 밀린 기본급 + 미지급 인센(양수만; 음수=과지급은 0).
  const totalPending = Math.max(0, payoutData?.retainerPending ?? 0) + Math.max(0, payoutData?.bonusPending ?? 0);
  const remaining = stats.bonus - totalPaid;

  return (
    <div className="affiliate-stats">
      <div className={`affiliate-stats__container${activeTab === 'scheduler' ? ' affiliate-stats__container--wide' : ''}`}>
        <div className="affiliate-stats__header">
          {/* Base handle (immutable identifier) — click to open Instagram. */}
          <h1 className="affiliate-stats__title">
            <a
              className="affiliate-stats__handle-link"
              href={`https://instagram.com/${igHandle.replace(/^@/, '')}`}
              target="_blank"
              rel="noreferrer"
              title="Open Instagram in a new tab"
            >
              @{igHandle}
            </a>
            {countryToFlag(country) && (
              <span
                className="affiliate-stats__country"
                title={countryName(country)}
                aria-label={countryName(country)}
              >
                {countryToFlag(country)}
              </span>
            )}
          </h1>

          {/* Extra display handles (editable). Each links to Instagram; removing
              one keeps it in history rather than deleting it. */}
          <div className="affiliate-stats__handles">
            {displayHandles.map(h => (
              <span key={h} className="affiliate-stats__handle-chip">
                <a
                  className="affiliate-stats__handle-chip-link"
                  href={`https://instagram.com/${h.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open Instagram in a new tab"
                >
                  @{h}
                </a>
                <button
                  type="button"
                  className="affiliate-stats__handle-remove"
                  onClick={() => removeHandle(h)}
                  aria-label={`Remove @${h}`}
                  title="Remove (kept in history)"
                >
                  ×
                </button>
              </span>
            ))}

            <span className="affiliate-stats__handle-add">
              <input
                type="text"
                className="affiliate-stats__handle-input"
                placeholder="add handle…"
                value={newHandle}
                disabled={addingHandle}
                onChange={e => setNewHandle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addHandle(); }}
              />
              <button
                type="button"
                className="affiliate-stats__handle-add-btn"
                onClick={addHandle}
                disabled={addingHandle || !newHandle.trim()}
              >
                {addingHandle ? '…' : 'Add'}
              </button>
            </span>
          </div>

          {handleHistory.length > 0 && (
            <div className="affiliate-stats__handle-history">
              <button
                type="button"
                className="affiliate-stats__handle-history-toggle"
                onClick={() => setShowHistory(s => !s)}
              >
                {showHistory ? 'Hide' : 'Show'} previous handles ({handleHistory.length})
              </button>
              {showHistory && (
                <ul className="affiliate-stats__handle-history-list">
                  {handleHistory.map((h, i) => (
                    <li key={`${h.handle}-${i}`}>
                      <a
                        href={`https://instagram.com/${h.handle.replace(/^@/, '')}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        @{h.handle}
                      </a>
                      {h.removedAt && (
                        <span className="affiliate-stats__handle-history-date">
                          {' '}· removed {new Date(h.removedAt).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="affiliate-stats__subtitle">Affiliate Dashboard</p>
        </div>

        {/* Detail tabs: Overview (existing dashboard) / 📅 Scheduler & Drafts */}
        <div className="affiliate-stats__tabs">
          <button
            type="button"
            className={`affiliate-stats__tab${activeTab === 'overview' ? ' affiliate-stats__tab--active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            type="button"
            className={`affiliate-stats__tab${activeTab === 'scheduler' ? ' affiliate-stats__tab--active' : ''}`}
            onClick={() => setActiveTab('scheduler')}
          >
            📅 Schedule &amp; Drafts
          </button>
          <button
            type="button"
            className={`affiliate-stats__tab${activeTab === 'payouts' ? ' affiliate-stats__tab--active' : ''}`}
            onClick={() => setActiveTab('payouts')}
          >
            💰 Payouts
          </button>
        </div>

        {activeTab === 'scheduler' && <SchedulerTab handle={handle} />}

        {activeTab === 'overview' && (<>
        {/* 계약 종료일 바는 크리에이터 개별 페이지에서 숨긴다(2026-08-28).
           "term through / days left" 표시가 장기 계약자에게 "잘리나?" 불안을 줌(Tillmann·Josh CS).
           종료·연장은 우리가 조용히 관리(각 텀 보너스 $200 기준 자동 체크 + 종료 1주 전 알림),
           관리자는 통합 overview(/affiliates)의 Contract End 컬럼에서 확인·편집한다. */}
        {/* Customers section */}
        <div className="affiliate-stats__section">
          <h2 className="affiliate-stats__section-title">
            Customers
            <span className="affiliate-stats__survey-term" title="Survey search keyword (case-insensitive match)">
              🔎 Survey: <strong>{searchKeyword}</strong>
            </span>
            <span className="affiliate-stats__section-sub">(Promo: {promoCode} · Ref: ?ref={igHandle})</span>
          </h2>
          <div className="affiliate-stats__cards">
            <button
              type="button"
              className="affiliate-stats__card affiliate-stats__card--clickable"
              onClick={openSignups}
              title="Click to review matched survey signups and remove false positives"
            >
              <p className="affiliate-stats__card-label">Total Signups</p>
              <p className="affiliate-stats__card-value">{totalSignupsOf(stats)}</p>
              <p className="affiliate-stats__card-hint">Review ›</p>
            </button>
            <div className="affiliate-stats__card">
              <p className="affiliate-stats__card-label">Paid Customers</p>
              <p className="affiliate-stats__card-value">{stats.totalCustomers}</p>
            </div>
            <div className="affiliate-stats__card">
              <p className="affiliate-stats__card-label">Conversion Rate</p>
              <p className="affiliate-stats__card-value">
                {totalSignupsOf(stats) > 0
                  ? ((stats.totalCustomers / totalSignupsOf(stats)) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
          </div>
          <div className="affiliate-stats__breakdown">
            <p className="affiliate-stats__breakdown-item">
              Promo code: <strong>[Paid: {stats.breakdown.promoCode}]</strong>
            </p>
            <p className="affiliate-stats__breakdown-item">
              Survey only: <strong>[Paid: {stats.breakdown.surveyOnly}, Sign Up: {stats.survey?.totalSignups ?? 0}]</strong>
            </p>
            <p className="affiliate-stats__breakdown-item">
              Tracking URL only: <strong>[Paid: {stats.breakdown.refOnly}, Sign Up: {stats.refLink?.totalSignups ?? 0}]</strong>
            </p>
          </div>

          {/* 유료 전환자 국가 분포(도넛+범례). 꼬리 국가가 많아 라벨 겹침 → 상위 8개국 + 나머지 "Others"로
              묶고, 라벨은 차트가 아닌 우측 범례로 뺀다. 파이 조각은 국가별로 서로 다른 색(구분 목적).
              타겟(북미/유럽) 강조는 색이 아니라 범례 국가명 초록 + 상단 % 요약으로만. */}
          {Array.isArray(stats.countryDistribution) && stats.countryDistribution.length > 0 && (() => {
            const dist = [...stats.countryDistribution].sort((a, b) => b.count - a.count);
            const total = dist.reduce((s, d) => s + d.count, 0);
            const TARGET = new Set(['US', 'CA', 'GB', 'IE', 'AU', 'NZ', 'FR', 'DE', 'ES', 'IT', 'NL', 'SE', 'NO', 'DK', 'FI', 'CH', 'AT', 'BE', 'PT', 'PL', 'CZ']);
            // 국가 구분용 팔레트(서로 잘 구별되는 8색). Others는 항상 연회색.
            const PALETTE = ['#2f9e5e', '#3b82c4', '#e0803a', '#8a6dbf', '#d64f8a', '#4bb3a8', '#c9a94f', '#6b7688'];
            // 상위 8개국 + 나머지 합산 Others. Unknown은 있으면 Others 쪽에 흡수(개별 노출 안 함).
            const TOP_N = 8;
            const named = dist.filter(d => d.country !== 'Unknown');
            const top = named.slice(0, TOP_N);
            const restCount = named.slice(TOP_N).reduce((s, d) => s + d.count, 0)
              + dist.filter(d => d.country === 'Unknown').reduce((s, d) => s + d.count, 0);
            const rows = top.map(d => ({
              cc: d.country,
              label: `${countryToFlag(d.country)} ${countryName(d.country) || d.country}`,
              value: d.count,
              target: TARGET.has(d.country),
            }));
            if (restCount > 0) rows.push({ cc: 'Others', label: 'Others', value: restCount, target: false });
            // 색을 한 번만 계산해 맵으로 고정(차트·범례가 같은 색을 쓰도록). 국가별로 순차 팔레트, Others만 회색.
            let pi = 0;
            const colorMap: Record<string, string> = {};
            for (const r of rows) {
              colorMap[r.cc] = r.cc === 'Others' ? '#d9dee7' : PALETTE[pi++ % PALETTE.length];
            }
            const colorFor = (cc: string) => colorMap[cc] || '#d9dee7';
            const chartData = rows.map(r => ({ name: r.label, cc: r.cc, value: r.value }));
            // 타겟(북미/유럽) 비중 = 목표 지표
            const targetShare = named.filter(d => TARGET.has(d.country)).reduce((s, d) => s + d.count, 0);
            const pct = (v: number) => ((v / total) * 100).toFixed(total >= 200 ? 1 : 0);
            return (
              <div className="affiliate-stats__country-dist">
                <h3 className="affiliate-stats__country-title">
                  Paid customers by country
                  <span className="affiliate-stats__country-sub"> · where your buyers actually are (goal: North America / Europe)</span>
                </h3>
                <p className="affiliate-stats__country-goal">
                  <strong className="affiliate-stats__country-goal-pct">{pct(targetShare)}%</strong> of your buyers are in North America / Europe / other target markets
                </p>
                <div className="affiliate-stats__country-flex">
                  <div className="affiliate-stats__country-chart">
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={54}
                          outerRadius={92}
                          paddingAngle={1}
                          stroke="none"
                        >
                          {chartData.map((d) => <Cell key={d.cc} fill={colorFor(d.cc)} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => [`${v} (${pct(v)}%)`, 'Paid customers']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="affiliate-stats__country-legend">
                    {rows.map(r => (
                      <li key={r.cc} className="affiliate-stats__country-legend-item">
                        <span className="affiliate-stats__country-dot" style={{ background: colorFor(r.cc) }} />
                        <span className={`affiliate-stats__country-name${r.target ? ' is-target' : ''}`}>{r.label}</span>
                        {r.target && <span className="affiliate-stats__country-badge">target</span>}
                        <span className="affiliate-stats__country-val">{r.value} <span className="affiliate-stats__country-pct">({pct(r.value)}%)</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Revenue section */}
        <div className="affiliate-stats__section">
          <h2 className="affiliate-stats__section-title">Revenue</h2>
          {/* When a rate change applies (joe after cutoff), split the bonus by tier
              so it's clear the higher rate only covers post-cutoff payments. */}
          {stats.bonusBreakdown && stats.bonusBreakdown.current.revenue > 0 ? (
            <>
              <div className="affiliate-stats__cards">
                <div className="affiliate-stats__card">
                  <p className="affiliate-stats__card-label">
                    Before {formatShortDate(stats.bonusBreakdown.cutoff)} · {pct(stats.bonusBreakdown.legacy.rate)}
                  </p>
                  <p className="affiliate-stats__card-value">{formatUSD(stats.bonusBreakdown.legacy.bonus)}</p>
                  <p className="affiliate-stats__breakdown-item">on {formatUSD(stats.bonusBreakdown.legacy.revenue)} revenue</p>
                </div>
                <div className="affiliate-stats__card affiliate-stats__card--bonus">
                  <p className="affiliate-stats__card-label">
                    From {formatShortDate(stats.bonusBreakdown.cutoff)} · {pct(stats.bonusBreakdown.current.rate)}
                  </p>
                  <p className="affiliate-stats__card-value">{formatUSD(stats.bonusBreakdown.current.bonus)}</p>
                  <p className="affiliate-stats__breakdown-item">on {formatUSD(stats.bonusBreakdown.current.revenue)} revenue</p>
                </div>
              </div>
              <p className="affiliate-stats__footnote">
                Total bonus: <strong>{formatUSD(stats.bonus)}</strong>
                {typeof stats.blendedRate === 'number' && ` (blended ${pct(stats.blendedRate)})`}
              </p>
            </>
          ) : (
            <div className="affiliate-stats__cards">
              <div className="affiliate-stats__card affiliate-stats__card--bonus">
                <p className="affiliate-stats__card-label">
                  Your Bonus{typeof stats.blendedRate === 'number' ? ` (${pct(stats.blendedRate)})` : ' (20%)'}
                </p>
                <p className="affiliate-stats__card-value">{formatUSD(stats.bonus)}</p>
              </div>
            </div>
          )}
        </div>

        </>)}

        {/* Payouts tab (split out of Overview into its own tab) */}
        {activeTab === 'payouts' && (
        <div className="affiliate-stats__section">
          {/* ── 크리에이터 셀프서브: Stripe 계좌 연결 ── */}
          <div className="affiliate-stats__payout-connect">
            <h2 className="affiliate-stats__section-title">💳 Get paid via Stripe</h2>
            {(() => {
              if (payoutStatus?.payoutsEnabled) {
                return <p className="affiliate-stats__connect-done">✅ Your payout account is connected. You'll receive payouts automatically — no more invoices needed.</p>;
              }
              if (payoutStatus && !payoutStatus.notStarted && payoutStatus.detailsSubmitted) {
                return <p className="affiliate-stats__connect-pending">⏳ Your details are submitted and under review by Stripe. This usually clears within a day.</p>;
              }
              if (payoutStatus && !payoutStatus.notStarted) {
                return (
                  <div className="affiliate-stats__connect-box">
                    <p>You've started connecting but haven't finished. Continue below to complete it.</p>
                    <button type="button" className="affiliate-stats__connect-btn" disabled={onboarding} onClick={startPayoutOnboarding}>
                      {onboarding ? 'Opening…' : 'Continue setup'}
                    </button>
                  </div>
                );
              }
              // not started
              return (
                <div className="affiliate-stats__connect-box">
                  <p>We now pay through Stripe instead of PayPal invoices — <b>you keep more of your pay</b> (no ~8% international/FX fees), payouts come <b>automatically</b>, and your info is handled securely. Connect your account once (~3 min):</p>
                  <div className="affiliate-stats__connect-row">
                    <select className="affiliate-stats__connect-select" value={onboardCountry} onChange={(e) => setOnboardCountry(e.target.value)}>
                      <option value="">Select your bank's country…</option>
                      {PAYOUT_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                    <button type="button" className="affiliate-stats__connect-btn" disabled={onboarding || !onboardCountry} onClick={startPayoutOnboarding}>
                      {onboarding ? 'Opening…' : 'Connect payout account'}
                    </button>
                  </div>
                  <p className="affiliate-stats__connect-note">Don't see your country? Let Heeyoon know on Slack — we'll keep you on PayPal for now.</p>
                </div>
              );
            })()}
          </div>

          <h2 className="affiliate-stats__section-title">Payouts <span className="affiliate-stats__section-sub" style={{ color: '#E65100' }}>⚠️ For Ssemble admins only — please do not edit</span></h2>
          <div className="affiliate-stats__summary-wrap">
            <table className="affiliate-stats__summary">
              <thead>
                <tr>
                  <th></th>
                  <th>Paid</th>
                  <th>Pending</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Retainer</th>
                  <td>{formatUSD(payoutData?.totalRetainer ?? 0)}</td>
                  <td style={{ color: (payoutData?.retainerPending ?? 0) > 0 ? '#b8730b' : undefined, fontWeight: (payoutData?.retainerPending ?? 0) > 0 ? 600 : undefined }}>
                    {formatUSD(Math.max(0, payoutData?.retainerPending ?? 0))}
                    {(payoutData?.retainerPending ?? 0) > 0 && payoutData?.retainerOwedWeeks && payoutData.retainerOwedWeeks.length > 0 && (
                      <span style={{ fontSize: '11px', color: '#8b8592', marginLeft: '6px' }}>
                        ({payoutData.retainerOwedWeeks.map((w) => `W${w}`).join(', ')})
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Bonus</th>
                  <td>{formatUSD(payoutData?.totalBonus ?? 0)}</td>
                  {/* Pending bonus. Positive = still owed to creator (amber). Negative = overpaid
                      (paid more than the sales generated so far); show the negative value in red with
                      a note so it's clear the future bonus will offset it, not that new money is owed. */}
                  {(payoutData?.bonusPending ?? 0) < 0 ? (
                    <td style={{ color: '#c0453f', fontWeight: 600 }}>
                      {formatUSD(payoutData?.bonusPending ?? 0)}
                      <span
                        className="affiliate-stats__pending-note"
                        title="You've been paid more bonus than the sales generated so far. This isn't money you owe — it just means upcoming bonus will first offset this difference before new bonus is paid out."
                      > (overpaid — will offset future bonus)</span>
                    </td>
                  ) : (
                    <td style={{ color: (payoutData?.bonusPending ?? 0) > 0 ? '#b8730b' : undefined, fontWeight: (payoutData?.bonusPending ?? 0) > 0 ? 600 : undefined }}>
                      {formatUSD(Math.max(0, payoutData?.bonusPending ?? 0))}
                    </td>
                  )}
                </tr>
                <tr className="affiliate-stats__summary-total">
                  <th>Total</th>
                  <td>{formatUSD(totalPaid)}</td>
                  <td style={{ color: totalPending > 0 ? '#b8730b' : undefined }}>{formatUSD(totalPending)}</td>
                </tr>
                <tr className="affiliate-stats__summary-grand">
                  <th>Grand Total</th>
                  <td colSpan={2}>{formatUSD(totalPaid + totalPending)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Retainer / Bonus sub-tabs */}
          <div className="affiliate-stats__subtabs">
            <button
              type="button"
              className={`affiliate-stats__subtab${payoutKind === 'retainer' ? ' affiliate-stats__subtab--active' : ''}`}
              onClick={() => setPayoutKind('retainer')}
            >
              Retainer {payoutData?.retainer ? `(${payoutData.retainer.length})` : ''}
            </button>
            <button
              type="button"
              className={`affiliate-stats__subtab${payoutKind === 'bonus' ? ' affiliate-stats__subtab--active' : ''}`}
              onClick={() => setPayoutKind('bonus')}
            >
              Bonus {payoutData?.bonus ? `(${payoutData.bonus.length})` : ''}
            </button>
          </div>

          {/* Add payout form (adds to the active sub-tab kind) */}
          <form className="affiliate-stats__payout-form" onSubmit={handleAddPayout}>
            <input
              type="number"
              step="0.01"
              min="0.01"
              placeholder={`${payoutKind === 'retainer' ? 'Retainer' : 'Bonus'} amount (USD)`}
              value={payoutAmount}
              onChange={e => setPayoutAmount(e.target.value)}
              className="affiliate-stats__input"
              required
            />
            <input
              type="date"
              value={payoutDate}
              onChange={e => setPayoutDate(e.target.value)}
              className="affiliate-stats__input"
              required
            />
            <input
              type="text"
              placeholder="PayPal invoice / note (optional)"
              value={payoutNote}
              onChange={e => setPayoutNote(e.target.value)}
              className="affiliate-stats__input"
            />
            <button type="submit" className="affiliate-stats__btn" disabled={submitting}>
              {submitting ? 'Adding...' : `Add ${payoutKind === 'retainer' ? 'Retainer' : 'Bonus'}`}
            </button>
          </form>

          {/* Payout history for the active sub-tab */}
          {(() => {
            const list = payoutKind === 'retainer' ? (payoutData?.retainer || []) : (payoutData?.bonus || []);
            const owedWeeks = payoutKind === 'retainer' ? (payoutData?.retainerOwedWeeks || []) : [];
            // 계정 전환자(sourceAccount 2개+)만 Week 옆에 @계정을 표시(단일계정이면 week만).
            const multiAccount = new Set(list.map(p => (p.sourceAccount || '').toLowerCase()).filter(Boolean)).size >= 2;
            // retainer 탭에선 지급 0건이어도 밀린 주차가 있으면 테이블 표시(연체분을 봐야 함).
            if (!list.length && !owedWeeks.length) return <p className="affiliate-stats__footnote">No {payoutKind} payouts yet.</p>;
            return (
            <table className="affiliate-stats__table">
              <thead>
                <tr>
                  <th>Date</th>
                  {payoutKind === 'retainer' && <th>Week</th>}
                  <th>Amount</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {/* 밀린(unpaid) retainer 주차 — 최신순(내림차순)으로 맨 위에 주황색 표시(아직 안 준 주차). */}
                {payoutKind === 'retainer' && [...(payoutData?.retainerOwedWeeks || [])].sort((a, b) => b - a).map(w => {
                  const amt = w === 1 ? 100 : w === 2 ? 400 : 500;  // W3+ (연장 포함) $500
                  return (
                    <tr key={`owed-${w}`} className="affiliate-stats__payout-row--unpaid">
                      <td style={{ color: '#b8730b' }}>—</td>
                      <td style={{ color: '#b8730b', fontWeight: 600 }}>W{w}</td>
                      <td style={{ color: '#b8730b', fontWeight: 600 }}>{formatUSD(amt)}</td>
                      <td style={{ color: '#b8730b' }}>⚠️ Unpaid</td>
                    </tr>
                  );
                })}
                {list.map(p => (
                  <tr key={p._id}>
                    <td>{new Date(p.date).toLocaleDateString()}</td>
                    {payoutKind === 'retainer' && (
                      <td>
                        {p.week ? `W${p.week}` : '-'}
                        {multiAccount && p.sourceAccount && (
                          <span style={{ marginLeft: 6, fontSize: '0.78em', color: '#888' }}>@{p.sourceAccount}</span>
                        )}
                      </td>
                    )}
                    <td
                      onDoubleClick={() => handleAmountDoubleClick(p)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingAmountId === p._id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={editingAmountValue}
                          onChange={e => setEditingAmountValue(e.target.value)}
                          onBlur={() => handleAmountSave(p._id)}
                          onKeyDown={e => { if (e.key === 'Enter') handleAmountSave(p._id); if (e.key === 'Escape') setEditingAmountId(null); }}
                          onDoubleClick={e => e.stopPropagation()}
                          onClick={e => e.stopPropagation()}
                          className="affiliate-stats__input"
                          style={{ margin: 0, padding: '4px 8px', width: '100%' }}
                          autoFocus
                        />
                      ) : (
                        formatUSD(p.amount)
                      )}
                    </td>
                    <td
                      onDoubleClick={() => handleNoteDoubleClick(p)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingNoteId === p._id ? (
                        <input
                          type="text"
                          value={editingNoteValue}
                          onChange={e => setEditingNoteValue(e.target.value)}
                          onBlur={() => handleNoteSave(p._id)}
                          onKeyDown={e => { if (e.key === 'Enter') handleNoteSave(p._id); if (e.key === 'Escape') setEditingNoteId(null); }}
                          onDoubleClick={e => e.stopPropagation()}
                          onClick={e => e.stopPropagation()}
                          className="affiliate-stats__input"
                          style={{ margin: 0, padding: '4px 8px', width: '100%' }}
                          autoFocus
                        />
                      ) : (
                        p.note || '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}
        </div>
        )}

        {activeTab === 'overview' && (<>
        {/* Daily breakdown section (monthly navigation) */}
        <div className="affiliate-stats__section">
          <div className="affiliate-stats__month-nav">
            <button
              type="button"
              className="affiliate-stats__month-arrow"
              onClick={goPrevMonth}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="affiliate-stats__month-label">{MONTH_NAMES[viewMonth - 1]} {viewYear}</span>
            <button
              type="button"
              className="affiliate-stats__month-arrow"
              onClick={goNextMonth}
              disabled={isCurrentMonth}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          {/* Monthly chart: Bonus as the main line, Signups/Conversions as secondary bars. */}
          {daily && daily.days.length > 0 && (
            <div className="affiliate-stats__chart" style={{ opacity: dailyLoading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart
                  data={daily.days.map(d => ({
                    day: Number(d.date.slice(-2)),
                    signups: d.signups,
                    conversions: d.paid,
                    bonus: Number(d.bonus.toFixed(2))
                  }))}
                  margin={{ top: 8, right: 8, bottom: 4, left: -8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickLine={false} axisLine={{ stroke: 'var(--border-primary)' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickLine={false} axisLine={false} width={34} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#2E7D32' }} tickLine={false} axisLine={false} width={44} tickFormatter={(v: number) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)' }}
                    formatter={(value: number, name: string) => name === 'Bonus' ? [`$${value}`, name] : [value, name]}
                    labelFormatter={(d) => `${MONTH_NAMES[viewMonth - 1]} ${d}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="signups" name="Signups" fill="#c7d2e8" radius={[2, 2, 0, 0]} maxBarSize={18} />
                  <Bar yAxisId="left" dataKey="conversions" name="Conversions" fill="#8fa8d6" radius={[2, 2, 0, 0]} maxBarSize={18} />
                  <Line yAxisId="right" type="monotone" dataKey="bonus" name="Bonus" stroke="#2E7D32" strokeWidth={2.5} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          <div
            className="affiliate-stats__table-wrap"
            style={{ opacity: dailyLoading ? 0.5 : 1, transition: 'opacity 0.15s' }}
          >
            <table className="affiliate-stats__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>New Signups</th>
                  <th>Paid Conversions</th>
                  <th>Revenue</th>
                  <th>Rate</th>
                  <th>Bonus</th>
                </tr>
              </thead>
              <tbody>
                {daily && daily.days.length > 0 ? (
                  daily.days.map((d, i) => {
                    // Insert a divider row right before the first day on/after the
                    // cutoff, so the rate increase reads as a clear boundary.
                    const cutoff = daily.rateCutoff;
                    const showDivider = !!cutoff
                      && d.date >= cutoff
                      && (i === 0 || daily.days[i - 1].date < cutoff);
                    const isCurrentRate = typeof d.rate === 'number' && d.rate >= JOE_BONUS_RATE;
                    return (
                      <React.Fragment key={d.date}>
                        {showDivider && (
                          <tr className="affiliate-stats__rate-divider">
                            <td colSpan={6}>
                              Rate increased to {pct(JOE_BONUS_RATE)} (from {formatShortDate(cutoff!)})
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>{d.date}</td>
                          <td>{d.signups}</td>
                          <td>{d.paid}</td>
                          <td>{formatUSD(d.revenue)}</td>
                          <td>
                            <span className={isCurrentRate ? 'affiliate-stats__rate-badge affiliate-stats__rate-badge--up' : 'affiliate-stats__rate-badge'}>
                              {typeof d.rate === 'number' ? pct(d.rate) : '—'}
                            </span>
                          </td>
                          <td>{formatUSD(d.bonus)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>
                      No performance data for this month
                    </td>
                  </tr>
                )}
              </tbody>
              {daily && daily.days.length > 0 && (
                <tfoot>
                  <tr className="affiliate-stats__table-foot">
                    <td>Total</td>
                    <td>{daily.totals.signups}</td>
                    <td>{daily.totals.paid}</td>
                    <td>{formatUSD(daily.totals.revenue)}</td>
                    <td>—</td>
                    <td>{formatUSD(daily.totals.bonus)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {daily && !daily.promoSignupsTracked && (
            <p className="affiliate-stats__footnote">
              * Promo code signup dates aren't tracked, so they're excluded from the New Signups count.
            </p>
          )}
          {daily && daily.rateCutoff && (
            <p className="affiliate-stats__footnote">
              * Bonus rate increased from {pct(0.20)} to {pct(JOE_BONUS_RATE)} for payments on or after {formatShortDate(daily.rateCutoff)}, {daily.year}. Earlier payments stay at {pct(0.20)}.
            </p>
          )}
        </div>

        {/* Audience distribution images (multiple) */}
        <div className="affiliate-stats__section">
          <h2 className="affiliate-stats__section-title">Audience Distribution</h2>
          {audienceImages.length > 0 && (
            <div className="affiliate-stats__audience-grid">
              {audienceImages.map(img => (
                <div key={img.id} className="affiliate-stats__audience-item">
                  <img
                    src={img.imageUrl}
                    alt="Audience distribution"
                    className="affiliate-stats__audience-img affiliate-stats__audience-img--clickable"
                    onClick={() => setLightboxUrl(img.imageUrl)}
                    title="Click to enlarge"
                  />
                  <button
                    type="button"
                    className="affiliate-stats__audience-remove"
                    onClick={() => handleDeleteImage(img.id)}
                    aria-label="Remove image"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {audienceImages.length < AUDIENCE_IMAGE_MAX ? (
            <label
              className={`affiliate-stats__dropzone${dragActive ? ' affiliate-stats__dropzone--active' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
              onDrop={handleImageDrop}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files && e.target.files.length > 0) uploadAudienceImages(e.target.files); e.target.value = ''; }}
              />
              <p className="affiliate-stats__dropzone-text">
                {imageUploading
                  ? 'Uploading...'
                  : audienceImages.length > 0
                    ? `Drag & drop more images, or click to browse (${audienceImages.length}/${AUDIENCE_IMAGE_MAX})`
                    : 'Drag & drop your audience distribution images here, or click to browse'}
              </p>
            </label>
          ) : (
            <p className="affiliate-stats__footnote">Maximum {AUDIENCE_IMAGE_MAX} images reached. Remove one to add more.</p>
          )}
        </div>
        </>)}
      </div>

      {/* Lightbox: click an audience image to view it enlarged */}
      {lightboxUrl && (
        <div
          className="affiliate-stats__lightbox"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="affiliate-stats__lightbox-close"
            onClick={() => setLightboxUrl(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt="Audience distribution enlarged"
            className="affiliate-stats__lightbox-img"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Survey signups review modal: eyeball matched responses, exclude false positives */}
      {signupsOpen && (
        <div
          className="affiliate-stats__modal-overlay"
          onClick={() => setSignupsOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="affiliate-stats__modal" onClick={e => e.stopPropagation()}>
            <div className="affiliate-stats__modal-header">
              <div>
                <h3 className="affiliate-stats__modal-title">Survey Signups</h3>
                <p className="affiliate-stats__modal-sub">
                  Matched by survey keyword <strong>“{searchKeyword}”</strong> (case-insensitive). Excluded rows are removed from all stats.
                </p>
              </div>
              <button
                type="button"
                className="affiliate-stats__modal-close"
                onClick={() => setSignupsOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {signupsLoading && signups.length === 0 ? (
              <p className="affiliate-stats__modal-loading">Loading…</p>
            ) : signups.length === 0 ? (
              <p className="affiliate-stats__modal-loading">No matched survey signups.</p>
            ) : (
              <>
                <p className="affiliate-stats__modal-count">
                  {signups.filter(s => !s.excluded).length} counted
                  {signups.some(s => s.excluded) && ` · ${signups.filter(s => s.excluded).length} excluded`}
                </p>
                <div className="affiliate-stats__modal-list">
                  {signups.map(s => (
                    <div
                      key={s.token}
                      className={`affiliate-stats__signup${s.excluded ? ' affiliate-stats__signup--excluded' : ''}`}
                    >
                      <div className="affiliate-stats__signup-main">
                        <span className="affiliate-stats__signup-handle">
                          {s.surveyHandle || <em>(no handle)</em>}
                        </span>
                        {s.howDidYouHear && (
                          <span className="affiliate-stats__signup-hdyh"> · {s.howDidYouHear}</span>
                        )}
                        {s.paid && <span className="affiliate-stats__signup-paid">PAID</span>}
                      </div>
                      <button
                        type="button"
                        className={`affiliate-stats__signup-btn${s.excluded ? ' affiliate-stats__signup-btn--restore' : ''}`}
                        onClick={() => toggleExclusion(s)}
                        disabled={mutatingToken === s.token}
                      >
                        {mutatingToken === s.token ? '…' : s.excluded ? 'Restore' : 'Remove'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateStats;
